import {
  subscriptionPackageVersions,
  subscriptions,
  subscriptionStatusValues,
} from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

// Status labels - single source of truth
export const SUBSCRIPTION_STATUS_LABELS = {
  proposal: 'Tarjous',
  active: 'Aktiivinen',
  paused: 'Tauolla',
  cancelled: 'Peruttu',
} as const

// Export status type
export type SubscriptionStatus = (typeof subscriptionStatusValues)[number]

// Service status types for subscription overview
export type ServiceStatus =
  | 'completed'
  | 'scheduled'
  | 'due_soon'
  | 'overdue'
  | 'pending'

export type SubscriptionHealth = 'all_good' | 'needs_attention' | 'critical'

// Service status labels
export const SERVICE_STATUS_LABELS = {
  completed: 'Tehty',
  scheduled: 'Sovittu',
  due_soon: 'Tulossa',
  overdue: 'Myöhässä',
  pending: 'Odottaa',
} as const

// Service status colors
export const SERVICE_STATUS_COLORS = {
  completed: 'green',
  scheduled: 'blue',
  due_soon: 'yellow',
  overdue: 'red',
  pending: 'gray',
} as const

// Health status labels
export const HEALTH_STATUS_LABELS = {
  all_good: 'Kunnossa',
  needs_attention: 'Vaatii huomiota',
  critical: 'Kriittinen',
} as const

// Health status colors
export const HEALTH_STATUS_COLORS = {
  all_good: 'green',
  needs_attention: 'yellow',
  critical: 'red',
} as const

// Base schemas
export const selectSubscriptionSchema = createSelectSchema(subscriptions, {
  displayName: z.string().nullable(),
}).extend({
  opportunityId: z.number().nullable().optional(),
})
// Base schema for create/update operations (without validation)
export const baseInsertSubscriptionSchema = createInsertSchema(subscriptions)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
  })
  .extend({
    opportunityId: z.number().nullable().optional(),
    packageVersions: z
      .array(
        z.object({
          servicePackageVersionId: z.number(),
          quantity: z.number().min(1).default(1),
        }),
      )
      .min(1),
  })

export const insertSubscriptionSchema = baseInsertSubscriptionSchema

export const updateSubscriptionSchema = baseInsertSubscriptionSchema
  .omit({ packageVersions: true })
  .partial()

// Response schema for create/update operations (excludes audit fields, serializes dates)
export const subscriptionResponseSchema = selectSubscriptionSchema
  .omit({
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
  })
  .extend({
    startDate: z.string(),
    endDate: z.string().nullable(),
    termsAcceptedAt: z.string().nullable(),
  })

// Schema with relations for API responses
export const selectSubscriptionWithRelationsSchema = selectSubscriptionSchema
  .omit({
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
  })
  .extend({
    startDate: z.string(),
    endDate: z.string().nullable(),
    termsAcceptedAt: z.string().nullable(),
    basePriceMonthly: z.string().nullable(),
    effectivePriceMonthly: z.string().nullable(),
    property: z.object({
      id: z.number(),
      address: z
        .object({
          street: z.string().nullable(),
          city: z.string().nullable(),
          postalCode: z.string().nullable(),
        })
        .optional(),
    }),
    subscriptionPackageVersions: z.array(
      z.object({
        id: z.number(),
        quantity: z.number(),
        sortOrder: z.number(),
        servicePackageVersion: z.object({
          id: z.number(),
          versionNumber: z.number(),
          priceMonthly: z.string(),
          description: z.string().nullable().optional(),
        }),
      }),
    ),
  })

// Internal junction table schemas (not exposed in API responses)
export const selectSubscriptionPackageVersionSchema = createSelectSchema(
  subscriptionPackageVersions,
)
export const insertSubscriptionPackageVersionSchema = createInsertSchema(
  subscriptionPackageVersions,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

// Type exports
export type Subscription = z.infer<typeof selectSubscriptionSchema>
export type SubscriptionInsert = z.infer<typeof insertSubscriptionSchema>
export type SubscriptionUpdate = z.infer<typeof updateSubscriptionSchema>
export type SubscriptionResponse = z.infer<typeof subscriptionResponseSchema>
export type SubscriptionWithRelations = z.infer<
  typeof selectSubscriptionWithRelationsSchema
>

// Internal junction table types
export type SubscriptionPackageVersion = z.infer<
  typeof selectSubscriptionPackageVersionSchema
>
export type SubscriptionPackageVersionInsert = z.infer<
  typeof insertSubscriptionPackageVersionSchema
>
